import { DropdownIcon } from '@/assets/images';
import { AppIcon, UnreadBadge } from '@/components';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState } from 'react';
import { useChatObject } from '../../viewmodels';
import './scrollToBottomButton.scss';
import { getUserIdFromJid } from '@/shared/utils';
import { sendSeenForMessages } from '../../viewmodels/useSendSeen';

const AT_BOTTOM_THRESHOLD = 10;

const ScrollToBottomButton = memo(({ scrollRef, activeChatJid, messages }) => {
   const [isAtBottom, setIsAtBottom] = useState(true);
   const chat = useChatObject(getUserIdFromJid(activeChatJid));
   const unreadCount = chat?.unreadCount || 0;

   // ─── Scroll listener ──────────────────────────────────────────────────────

   useEffect(() => {
      const container = scrollRef.current?.getScrollContainer();
      if (!container) return;

      const handleScroll = () => {
         const atBottom = Math.abs(container.scrollTop) < AT_BOTTOM_THRESHOLD;
         setIsAtBottom(atBottom);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
   }, [scrollRef]);

   const handleBeforeJump = useCallback(() => {
      sendSeenForMessages({ messages, activeChatJid });
   }, [messages, activeChatJid]);

   // ─── Click ────────────────────────────────────────────────────────────────

   const handleClick = useCallback(() => {
      handleBeforeJump();
      scrollRef.current?.jumpToBottom({ smooth: true });
      setIsAtBottom(true);
   }, [scrollRef, handleBeforeJump]);

   const show = !isAtBottom;

   return (
      <button
         className={`stb-root ct-flexCenter ct-fcol ${show ? 'stb-visible' : ''}`}
         onClick={handleClick}
         aria-label={unreadCount > 0 ? `${unreadCount} new messages, scroll to bottom` : 'Scroll to bottom'}
      >
         <UnreadBadge count={unreadCount} className="stb-unread-badge" />
         <AppIcon size="lg" icon={<DropdownIcon />} />
      </button>
   );
});

ScrollToBottomButton.displayName = 'ScrollToBottomButton';

ScrollToBottomButton.propTypes = {
   scrollRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
   activeChatJid: PropTypes.string,
   messages: PropTypes.array,
};

export default ScrollToBottomButton;
